import { Sortable } from "./sortablejs";
import { Options } from "sortablejs";

export class InvalidIdError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = 'InvalidIdError';
	}
}

export class InvalidIndexError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = 'InvalidIndexError';
	}
}

interface CustomSortableOptions extends Options {
	/**
	 * Event when you press a key with a sortable element in focus.
	 * @param e The keypress event object.
	 */
	onKeyBindMove?: (e: KeyboardEvent) => void;
	/**
	 * Disables keybind events for this sortable if set to false.
	 */
	hasKeybinds?: boolean;
}

export default class CustomSortable extends Sortable {
	options: CustomSortableOptions;

	_onKeyDown(this: HTMLElement, e: KeyboardEvent) {
		const sortable = Sortable.get(this) as CustomSortable;
		if (
			!sortable.options.hasKeybinds
			|| sortable.options.disabled
			|| !e.key.startsWith('Arrow')
		) {
			return;
		}
		if (
			(sortable.options.direction === 'vertical'
				&& !(e.key === 'ArrowUp' || e.key === 'ArrowDown')
		) || (
			sortable.options.direction === 'horizontal'
				&& !(e.key === 'ArrowLeft' || e.key === 'ArrowRight'))
		) {
			return;
		}
		try {
			if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
				sortable.moveUp(
					(e.target as HTMLElement)
						.getAttribute(sortable.options.dataIdAttr),
					!!sortable.options.animation
				);
			} else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
				sortable.moveDown(
					(e.target as HTMLElement)
						.getAttribute(sortable.options.dataIdAttr),
					!!sortable.options.animation
				);
			}
			sortable.options.onKeyBindMove(e);
			(e.target as HTMLElement).focus();
		} catch (e: unknown) {
			if (e instanceof InvalidIdError || e instanceof InvalidIndexError) {
				return;
			}
		}
	};

	constructor(el: HTMLElement, options: CustomSortableOptions) {
		const defaults: CustomSortableOptions = {
			hasKeybinds: true,
		};
		super(el, { ...defaults, ...options });
		Sortable.utils.on(el, 'keydown', this._onKeyDown);
	}

	destroy() {
		Sortable.utils.off(this.el, 'keydown', this._onKeyDown);
		super.destroy();
	}

	sort(order: ReadonlyArray<string>, useAnimation?: boolean): void {
		const items = this.serialMap();
		const children = Array.from(this.el.children as HTMLCollectionOf<HTMLElement>)
			.map((e, i) => ({ e, i }))
			.filter(({ e }) => Sortable.utils.closest(e, this.options.draggable));

		useAnimation && this.captureAnimationState();
		order.slice(0, -1).forEach((id, index) => {
			this.swapNodes(this.el.children[children[index].i], items[id]);
		});
		useAnimation && this.animateAll();
	}

	private _swap(a: string, b: string, useAnimation?: boolean): void {
		if (![a, b].every(v => this.toArray().includes(v))) {
			throw new InvalidIdError('swap must receive two valid serialized items');
		}
		const items = this.serialMap();
		useAnimation && this.captureAnimationState();
		this.swapNodes(items[a], items[b]);
		useAnimation && this.animateAll();
	}

	moveUp(id: string, useAnimation?: boolean): void {
		const ids = this.toArray();
		if (!ids.includes(id)) {
			throw new InvalidIdError('moveUp must receive a valid serialized item');
		}
		const index = ids.indexOf(id);
		if (index <= 0) {
			throw new InvalidIndexError('item already at the start');
		}
		this._swap(id, ids[index - 1], useAnimation);
	}

	moveLeft: (id: string, useAnimation?: boolean) => void = this.moveUp;

	moveDown(id: string, useAnimation?: boolean): void {
		const ids = this.toArray();
		if (!ids.includes(id)) {
			throw new InvalidIdError('moveDown must receive a valid serialized item');
		}
		const index = ids.indexOf(id);
		if (index >= ids.length - 1) {
			throw new InvalidIndexError('item already at the end');
		}
		this._swap(id, ids[index + 1], useAnimation);
	}

	moveRight: (id: string, useAnimation?: boolean) => void = this.moveDown;

	children(): HTMLElement[] {
		return Array.from(this.el.children as HTMLCollectionOf<HTMLElement>)
			.filter(c => Sortable.utils.closest(c, this.options.draggable));
	}

	private serialMap(): { [p: string]: Element } {
		const children = this.children();
		return this.toArray().reduce((result, id, index) => ({
			...result,
			[id]: children[index]
		}), {});
	}

	private swapNodes(n1: Element, n2: Element): void {
		let p1 = n1.parentNode,
			p2 = n2.parentNode;
		if (!p1 || !p2 || p1.isEqualNode(n2) || p2.isEqualNode(n1)) return;
		let i1 = Sortable.utils.index(n1);
		let i2 = Sortable.utils.index(n2);
		if (p1.isEqualNode(p2) && i1 < i2) {
			i2++;
		}

		p1.insertBefore(n2, p1.children[i1]);
		p2.insertBefore(n1, p2.children[i2]);
	}

	static get(element: HTMLElement): CustomSortable | undefined {
		return Sortable.get(element) as CustomSortable;
	}
}
